import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Header.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { NavLink, Link } from 'react-router-dom';
import { FaBars, FaSearch } from 'react-icons/fa';
import { getAsideEntries } from '../../api/api';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMobile, setIsMobile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const [asideData, setAsideData] = useState([]);
  const [loading, setLoading] = useState(true);

  const sideMenuRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchAside = async () => {
      setLoading(true);
      try {
        const data = await getAsideEntries();
        setAsideData(data.sort((a, b) => a.sortOrder - b.sortOrder));
      } catch (err) {
        console.error('Błąd pobierania danych header:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAside();
  }, []);

  // Kliknięcie poza wyszukiwarką
  useEffect(() => {
    const handleClickOutsideSearch = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideSearch);
    return () =>
      document.removeEventListener('mousedown', handleClickOutsideSearch);
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const main = document.querySelector('main');
    if (!main) return;

    const elements = main.querySelectorAll('h1, h2, h3, p, span, li');
    const matches = [];

    elements.forEach((el) => {
      const text = el.innerText?.trim();
      if (text && text.toLowerCase().includes(value.toLowerCase())) {
        const preview = text.substring(0, 120);
        const displayText = preview + (text.length > 120 ? '...' : '');
        matches.push({ text: displayText, element: el });
      }
    });

    setResults(matches.slice(0, 6));
    setShowResults(true);
  };

  const scrollToElement = (element) => {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setShowResults(false);
    setSearchTerm('');
  };

  const grouped = asideData.reduce((acc, item) => {
    acc[item.type] = acc[item.type] || [];
    acc[item.type].push(item);
    return acc;
  }, {});

  const personal = grouped.personal?.[0];
  const social = grouped.social || [];

  const toggleMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
    setIsClosing(false);
  }, []);

  // Zamykanie menu po kliknięciu poza
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sideMenuRef.current &&
        !sideMenuRef.current.contains(event.target) &&
        !event.target.closest('.menu-icon')
      ) {
        setIsClosing(true);
        setTimeout(() => {
          setIsMenuOpen(false);
          setIsClosing(false);
        }, 200);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Poprawiona logika isMobile
  useEffect(() => {
    let timeoutId;

    const updateMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const width = window.innerWidth;

        setIsMobile(width <= 1080);
      }, 0);
    };

    updateMobile();
    window.addEventListener('resize', updateMobile);
    window.addEventListener('load', updateMobile);

    return () => {
      window.removeEventListener('resize', updateMobile);
      window.removeEventListener('load', updateMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  // Zamykanie menu, gdy przestajemy być mobile
  useEffect(() => {
    if (isMobile === false) {
      setIsMenuOpen(false);
      setIsClosing(false);
    }
  }, [isMobile]);

  // Nie renderuje sié nic, dopóki nie wiemy, czy mobile
  if (loading || isMobile === null) {
    return null;
  }

  return (
    <header className="Header">
      <Link to="/" className="logo-link">
        <h1>Portfolio</h1>
      </Link>

      {isMobile ? (
        <>
          <div className="menu-icon" onClick={toggleMenu}>
            <FaBars size={30} color="#de5f00" />
          </div>

          <nav
            ref={sideMenuRef}
            className={`side-menu ${isMenuOpen ? 'open' : ''} ${isClosing ? 'closing' : ''}`}
          >
            <button className="side-menu-close" onClick={toggleMenu}>
              <i className="fas fa-times"></i>
            </button>

            <div className="search-container-mobile" ref={searchRef}>
              <FaSearch className="search-icon-mobile" />
              <input
                type="text"
                placeholder="Szukaj..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {showResults && results.length > 0 && (
                <div className="search-results-mobile">
                  {results.map((item, index) => (
                    <div
                      key={index}
                      className="search-item"
                      onClick={() => scrollToElement(item.element)}
                    >
                      {item.text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="avatar"
              style={{
                backgroundImage: `url(${personal?.iconUrl || '/assets/images/awatar.jpg'})`,
              }}
            ></div>

            {personal ? (
              <>
                <h1 className="name-surname">{personal.title}</h1>
                <h2 className="status">{personal.subtitle}</h2>
              </>
            ) : (
              <div className="empty-personal">
                <p>Brak danych osobowych</p>
              </div>
            )}

            <div className="line-"></div>

            <NavLink
              to="/"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={toggleMenu}
            >
              O mnie
            </NavLink>
            <NavLink
              to="/Blog"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={toggleMenu}
            >
              Blog
            </NavLink>
            <NavLink
              to="/Projects"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={toggleMenu}
            >
              Projekty
            </NavLink>
            <NavLink
              to="/Resume"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={toggleMenu}
            >
              Resume
            </NavLink>
            <NavLink
              to="/Contact"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={toggleMenu}
            >
              Kontakt
            </NavLink>

            <div className="line-"></div>

            <div className="social-icons-p">
              <ul>
                {social.map((item) => {
                  const href = item.url?.startsWith('http')
                    ? item.url
                    : item.subtitle?.startsWith('http')
                      ? item.subtitle
                      : `https://${item.subtitle}`;
                  return (
                    <li key={item.id}>
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {item.iconUrl ? (
                          <img
                            src={item.iconUrl}
                            alt={item.title}
                            style={{ width: '32px', height: '32px' }}
                          />
                        ) : (
                          <i
                            className={`fa-brands fa-${item.title?.toLowerCase() || 'question'}`}
                          ></i>
                        )}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>
        </>
      ) : (
        <nav className="nav-desktop">
          <NavLink
            to="/"
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            O mnie
          </NavLink>
          <NavLink
            to="/Blog"
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            Blog
          </NavLink>
          <NavLink
            to="/Projects"
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            Projekty
          </NavLink>
          <NavLink
            to="/Resume"
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            Resume
          </NavLink>
          <NavLink
            to="/Contact"
            className={({ isActive }) => (isActive ? 'active' : '')}
          >
            Kontakt
          </NavLink>

          <div className="search-container" ref={searchRef}>
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Szukaj..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowResults(true)}
            />

            {showResults && results.length > 0 && (
              <div className="search-results">
                {results.map((item, index) => (
                  <div
                    key={index}
                    className="search-item"
                    onClick={() => scrollToElement(item.element)}
                  >
                    {item.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
